import {type MediaCardType} from '@wandb/weave/common/types/media';

import {type RampKey} from '../../../../util/useRampFlag';
import {BoundingBoxes} from './components/boundingBoxes/BoundingBoxes';
import {ChartTitle} from './components/ChartTitle';
import {ColumnContent} from './components/ColumnContent';
import {CompareControls} from './components/CompareControls';
import {DisableVideoStream} from './components/DisableVideoStream';
import {FanOut} from './components/FanOut';
import {ImageFitAndResolution} from './components/ImageFitAndResolution';
import {LayoutMode} from './components/LayoutMode';
import {Masks} from './components/masks/Masks';
import {MaxItemCount} from './components/MaxItemCount';
import {MaxRowCount} from './components/MaxRowCount';
import {MaxRuns} from './components/MaxRuns';
import {MediaKeys} from './components/MediaKeys';
import {NumberOfColumns} from './components/NumberOfColumns';
import {AxesHandedness} from './components/object3D/AxesHandedness';
import {Object3DBackgroundColor} from './components/object3D/Object3DBackgroundColor';
import {PointColor} from './components/object3D/PointColor';
import {OriginalSize} from './components/OriginalSize';
import {SliderKey} from './components/SliderKey';
import {SmoothImage} from './components/SmoothImage';
import {SnapToExistingStep} from './components/SnapToExistingStep';
import {StrideLength} from './components/StrideLength';
import {VideoPlaybackSync} from './components/VideoPlaybackSync';
import {VideoPlaybackSyncAutoplay} from './components/VideoPlaybackSyncAutoplay';
import {VideoPlaybackSyncLoop} from './components/VideoPlaybackSyncLoop';
import {VideoPlayerTimeStep} from './components/VideoPlayerTimeStep';
import {XAxis} from './components/XAxis';
import {YAxis} from './components/YAxis';
import {
  makeColumnCountComponentKeySelector,
  makeColumnCountShouldShowSelector,
  makeColumnCountShowOverridesSelector,
  makeGridSettingsComponentKeySelector,
  makeImageFitAndResolutionComponentKeySelector,
  makeLayoutModeComponentKeySelector,
  makeLayoutModeShowOverridesSelector,
  makeMaxGalleryItemsComponentKeySelector,
  makeMaxGalleryItemsShouldShowSelector,
  makeMaxGalleryItemsShowOverridesSelector,
  makeMaxRunsComponentKeySelector,
  makeMaxRunsShowOverridesSelector,
  makeMaxYAxisCountComponentKeySelector,
  makeMaxYAxisCountShouldShowSelector,
  makeMaxYAxisCountShowOverridesSelector,
  makeModeIsCompareShouldShowSelector,
  makeModeIsGalleryShouldShowSelector,
  makeModeIsGridShouldShowSelector,
  makeModeIsNotCompareShouldShowSelector,
  makeOriginalSizeComponentKeySelector,
  makeOriginalSizeShowOverridesSelector,
  makePixelatedComponentKeySelector,
  makePixelatedShowOverridesSelector,
  makeSliderKeyShowOverridesSelector,
  makeSliderSyncComponentKeySelector,
  makeSliderSyncShowOverridesSelector,
  makeSnapToExistingStepComponentKeySelector,
  makeSnapToExistingStepShowOverridesSelector,
  makeStepStrideLengthComponentKeySelector,
  makeStepStrideLengthShowOverridesSelector,
  makeUseRightHandedSystemComponentKeySelector,
  makeVideoPlaybackSyncAutoplayComponentKeySelector,
  makeVideoPlaybackSyncComponentKeySelector,
  makeVideoPlaybackSyncLoopComponentKeySelector,
  makeVideoPlayerTimeStepComponentKeySelector,
} from './mediaSettingsSelectors';
import {mediaSettings} from './settingFactory/mediaSettingsTable';
import {SliderSync} from './SliderSync';
import {
  type MediaSetting,
  type MediaSettingsSection,
  type MediaSettingsTab,
  SettingsLevel,
} from './types';

export const MEDIA_SETTINGS_TABS: MediaSettingsTab[] = [
  {
    title: 'Display',
    sections: [
      {
        title: 'Viewer settings',
        settings: [
          {
            component: ChartTitle,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            configKeys: ['chartTitle'],
          },
          {
            component: LayoutMode,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            configKeys: ['mode'],
            getComponentKeySelector: makeLayoutModeComponentKeySelector,
            showOverridesSelector: makeLayoutModeShowOverridesSelector,
          },
          {
            component: MediaKeys,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            // compare mode has its own controls for media keys
            shouldShowSelector: makeModeIsNotCompareShouldShowSelector,
            configKeys: ['mediaKeys'],
          },
          {
            component: SliderKey,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            configKeys: ['mediaSliderKey'],
            showOverridesSelector: makeSliderKeyShowOverridesSelector,
          },
          {
            component: Object3DBackgroundColor,
            mediaTypes: ['object3D'],
            configKeys: ['object3DBackgroundColor'],
          },
        ],
      },
      {
        title: 'Appearance',
        settings: [
          {
            component: SmoothImage,
            mediaTypes: ['image'],
            configKeys: ['pixelated'],
            getComponentKeySelector: makePixelatedComponentKeySelector,
            showOverridesSelector: makePixelatedShowOverridesSelector,
          },
          {
            component: PointColor,
            mediaTypes: ['object3D'],
            configKeys: ['pointColor'],
          },
        ],
      },
      {
        title: 'Navigation & playback',
        settings: [
          {
            component: SnapToExistingStep,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            configKeys: ['snapToExistingStep'],
            getComponentKeySelector: makeSnapToExistingStepComponentKeySelector,
            showOverridesSelector: makeSnapToExistingStepShowOverridesSelector,
          },
          {
            component: StrideLength,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            configKeys: ['stepStrideLength'],
            getComponentKeySelector: makeStepStrideLengthComponentKeySelector,
            showOverridesSelector: makeStepStrideLengthShowOverridesSelector,
          },
          {
            component: VideoPlayerTimeStep,
            mediaTypes: ['video'],
            configKeys: ['videoPlayerTimeStep'],
            getComponentKeySelector:
              makeVideoPlayerTimeStepComponentKeySelector,
          },
        ],
      },
    ],
  },
  {
    title: 'Layout',
    sections: [
      {
        title: 'Grid & organization',
        settings: [
          {
            component: NumberOfColumns,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShowSelector: makeColumnCountShouldShowSelector,
            configKeys: ['columnCount'],
            getComponentKeySelector: makeColumnCountComponentKeySelector,
            showOverridesSelector: makeColumnCountShowOverridesSelector,
          },
          {
            component: CompareControls,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            configKeys: ['compareSettings'],
            shouldShowSelector: makeModeIsCompareShouldShowSelector,
          },
          {
            component: FanOut,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            // UI-only restriction: fan-out only makes sense on a single panel,
            // even though compareSettings itself can be set at every level.
            settingsLevels: [SettingsLevel.PANEL],
            configKeys: ['compareSettings'],
            shouldShowSelector: makeModeIsCompareShouldShowSelector,
          },
          {
            component: ColumnContent,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShowSelector: makeModeIsGalleryShouldShowSelector,
            configKeys: ['gallerySettings'],
          },
          {
            component: XAxis,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShowSelector: makeModeIsGridShouldShowSelector,
            getComponentKeySelector: makeGridSettingsComponentKeySelector, // they're interdependent since we swap them if you select the same axis for both
            configKeys: ['gridSettings'],
          },
          {
            component: YAxis,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShowSelector: makeModeIsGridShouldShowSelector,
            getComponentKeySelector: makeGridSettingsComponentKeySelector, // ditto
            configKeys: ['gridSettings'],
          },
          {
            component: MaxRuns,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShowSelector: makeModeIsNotCompareShouldShowSelector,
            configKeys: ['maxRuns'],
            getComponentKeySelector: makeMaxRunsComponentKeySelector,
            showOverridesSelector: makeMaxRunsShowOverridesSelector,
          },
          {
            component: MaxItemCount,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShowSelector: makeMaxGalleryItemsShouldShowSelector,
            configKeys: ['maxGalleryItems'],
            getComponentKeySelector: makeMaxGalleryItemsComponentKeySelector,
            showOverridesSelector: makeMaxGalleryItemsShowOverridesSelector,
          },
          {
            component: MaxRowCount,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            // this is the grid version of MaxItemCount
            shouldShowSelector: makeMaxYAxisCountShouldShowSelector,
            configKeys: ['maxYAxisCount'],
            getComponentKeySelector: makeMaxYAxisCountComponentKeySelector,
            showOverridesSelector: makeMaxYAxisCountShowOverridesSelector,
          },
        ],
      },
      {
        title: 'Fit & alignment',
        settings: [
          {
            component: OriginalSize,
            mediaTypes: ['video'],
            shouldShowSelector: makeModeIsGalleryShouldShowSelector,
            getComponentKeySelector: makeOriginalSizeComponentKeySelector,
            showOverridesSelector: makeOriginalSizeShowOverridesSelector,
            configKeys: ['videoActualSize'],
          },
          {
            component: ImageFitAndResolution,
            mediaTypes: ['image'],
            configKeys: ['actualSize', 'fitToDimension', 'alwaysOriginal'],
            getComponentKeySelector:
              makeImageFitAndResolutionComponentKeySelector,
          },
          {
            component: DisableVideoStream,
            mediaTypes: ['video'],
            configKeys: ['disableVideoStream'],
            shouldShow: (_hasMasks, _hasBoundingBoxes, rampFlags) => {
              return rampFlags?.['video_streaming_entity'] ?? false;
            },
          },
          {
            component: AxesHandedness,
            mediaTypes: ['object3D'],
            configKeys: ['useRightHandedSystem'],
            getComponentKeySelector:
              makeUseRightHandedSystemComponentKeySelector,
          },
        ],
      },
    ],
  },
  {
    title: 'Sync',
    sections: [
      {
        title: 'Slider sync',
        settings: [
          {
            component: SliderSync,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            configKeys: ['sliderSync'],
            getComponentKeySelector: makeSliderSyncComponentKeySelector,
            showOverridesSelector: makeSliderSyncShowOverridesSelector,
          },
        ],
      },
      {
        title: 'Sync video playback',
        settings: [
          {
            component: VideoPlaybackSync,
            mediaTypes: ['video'],
            configKeys: ['videoPlaybackSync'],
            getComponentKeySelector: makeVideoPlaybackSyncComponentKeySelector,
            subsettings: [
              {
                component: VideoPlaybackSyncAutoplay,
                mediaTypes: ['video'],
                // visibility is handled at render time by the component itself
                configKeys: ['videoPlaybackSyncAutoplay'],
                getComponentKeySelector:
                  makeVideoPlaybackSyncAutoplayComponentKeySelector,
              },
              {
                component: VideoPlaybackSyncLoop,
                mediaTypes: ['video'],
                // visibility is handled at render time by the component itself
                configKeys: ['videoPlaybackSyncLoop'],
                getComponentKeySelector:
                  makeVideoPlaybackSyncLoopComponentKeySelector,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Overlays',
    sections: [
      {
        title: 'Bounding boxes',
        settings: [
          {
            component: BoundingBoxes,
            mediaTypes: ['image', 'object3D'],
            shouldShow: (_hasMasks, hasBoundingBoxes) => hasBoundingBoxes,
            configKeys: ['boundingBoxConfig'],
          },
        ],
      },
      {
        title: 'Masks',
        settings: [
          {
            component: Masks,
            mediaTypes: ['image'],
            shouldShow: (hasMasks, _hasBoundingBoxes) => hasMasks,
            configKeys: ['segmentationMaskConfig'],
          },
        ],
      },
    ],
  },
];

export function getMediaSettingsTabs(
  mediaTypes: MediaCardType[] | null,
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  settingsLevel: SettingsLevel,
  rampFlags?: Partial<Record<RampKey, boolean>> | undefined
): MediaSettingsTab[] {
  if (mediaTypes == null) {
    return [];
  }

  return MEDIA_SETTINGS_TABS.map(tab => ({
    ...tab,
    sections: filterSections(
      tab.sections,
      mediaTypes,
      hasMasks,
      hasBoundingBoxes,
      rampFlags,
      settingsLevel
    ),
  })).filter(tab => tab.sections.length > 0);
}

function filterSections(
  sections: MediaSettingsSection[],
  mediaTypes: MediaCardType[],
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  rampFlags: Partial<Record<RampKey, boolean>> | undefined,
  settingsLevel: SettingsLevel
): MediaSettingsSection[] {
  return sections
    .map(section => ({
      ...section,
      settings: filterSettings(
        section.settings,
        mediaTypes,
        hasMasks,
        hasBoundingBoxes,
        rampFlags,
        settingsLevel
      ),
    }))
    .filter(section => section.settings.length > 0);
}

function filterSettings(
  settings: MediaSetting[],
  mediaTypes: MediaCardType[],
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  rampFlags: Partial<Record<RampKey, boolean>> | undefined,
  settingsLevel: SettingsLevel,
  isSubsetting = false
): MediaSetting[] {
  return settings
    .filter(setting =>
      shouldShowSetting(
        setting,
        mediaTypes,
        hasMasks,
        hasBoundingBoxes,
        rampFlags,
        settingsLevel,
        isSubsetting
      )
    )
    .map(setting => {
      if (setting.subsettings) {
        return {
          ...setting,
          subsettings: filterSettings(
            setting.subsettings,
            mediaTypes,
            hasMasks,
            hasBoundingBoxes,
            rampFlags,
            settingsLevel,
            true // Mark as subsettings
          ),
        };
      }
      return setting;
    });
}
function getSettingLevels(setting: MediaSetting): SettingsLevel[] {
  return (
    setting.settingsLevels ??
    mediaSettings[setting.configKeys[0] as keyof typeof mediaSettings].levels
  );
}

function shouldShowSetting(
  setting: MediaSetting,
  mediaTypes: MediaCardType[],
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  rampFlags: Partial<Record<RampKey, boolean>> | undefined,
  settingsLevel: SettingsLevel,
  isSubsetting = false
) {
  const isRelevantSettingForMediaTypes = setting.mediaTypes.some(mediaType =>
    mediaTypes.includes(mediaType)
  );
  // For subsettings, skip the shouldShow evaluation - it will be handled at render time
  const isRelevantSettingForCustomShouldShow = isSubsetting
    ? true
    : (setting.shouldShow?.(hasMasks, hasBoundingBoxes, rampFlags) ?? true);
  const settingLevels = getSettingLevels(setting);
  const isRelevantSettingForSettingsLevel =
    settingLevels.includes(settingsLevel);
  const shouldShow =
    isRelevantSettingForMediaTypes &&
    isRelevantSettingForCustomShouldShow &&
    isRelevantSettingForSettingsLevel;
  return shouldShow;
}
